if (!customElements.get('localization-form')) {
  // Mirrors assets/icon-checkmark.svg. The country rows are built in JS (see
  // hydrateCountries) so this markup lives here rather than being inlined into
  // every row by Liquid, which used to cost ~227KB per page.
  const COUNTRY_CHECKMARK =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-checkmark" viewBox="0 0 12 9">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M11.35.643a.5.5 0 0 1 .006.707l-6.77 6.886a.5.5 0 0 1-.719-.006' +
    'L.638 4.845a.5.5 0 1 1 .724-.69l2.872 3.011 6.41-6.517a.5.5 0 0 1 .707-.006z" clip-rule="evenodd"/></svg>';

  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  // Shopify's `| json` filter HTML-escapes its output, so names arrive as e.g.
  // "Antigua &amp; Barbuda". Decoding before re-escaping keeps this correct
  // whether or not that filter escapes, instead of silently double-escaping.
  let entityDecoder = null;

  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        // Delegated: the country rows are built lazily by hydrateCountries(), so
        // there is nothing to bind per-link at construction time. This also keeps
        // working for the language selector, whose rows are still server-rendered.
        this.addEventListener('click', this.onItemClick.bind(this));
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer').classList.remove('country-selector-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        // Arrow keys can reach this before the panel has ever been opened, and
        // the country rows are built lazily — so make sure they exist first.
        this.hydrateCountries();

        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            if (itemToFocus) itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            if (itemToFocus) itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        const link = event.target.closest('a[data-value]');
        if (!link || !this.contains(link)) return;
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = link.dataset.value;
        if (form) form.submit();
      }

      escapeHTML(value) {
        return String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
      }

      decodeEntities(value) {
        const str = String(value);
        if (str.indexOf('&') === -1) return str;
        if (!entityDecoder) entityDecoder = document.createElement('textarea');
        entityDecoder.innerHTML = str;
        return entityDecoder.value;
      }

      // Decode-then-escape, so the output is correct exactly once.
      text(value) {
        return this.escapeHTML(this.decodeEntities(value));
      }

      buildCountryRow(entry, data, idPrefix) {
        const [iso, name, currencyIso, currencySymbol] = entry;
        const isCurrent = iso === data.current;
        const currency =
          data.showCurrencies && currencyIso
            ? '<span class="localization-form__currency motion-reduce">' +
              this.text(currencyIso) +
              ' ' +
              this.text(currencySymbol || '') +
              '</span>'
            : '';

        return (
          '<li class="disclosure__item" tabindex="-1">' +
          '<a class="link link--text disclosure__link caption-large focus-inset" href="#"' +
          (isCurrent ? ' aria-current="true"' : '') +
          ' data-value="' +
          this.escapeHTML(iso) +
          '" id="' +
          this.escapeHTML(idPrefix + '-' + iso) +
          '"><span class="disclosure__checkmark">' +
          (isCurrent ? COUNTRY_CHECKMARK : '') +
          '</span><span class="country">' +
          this.text(name) +
          '</span>' +
          currency +
          '</a></li>'
        );
      }

      /*
        Builds the country rows the first time this selector is opened. Runs
        synchronously off an inline JSON payload rendered by
        snippets/country-localization.liquid — no network request, so this adds
        no failure mode the panel did not already have (it is only ever shown by
        openSelector() in the first place).
      */
      hydrateCountries() {
        const list = this.querySelector('[data-country-list]');
        if (!list || list.dataset.hydrated === 'true') return;

        const source = this.querySelector('script[data-country-data]');
        if (!source) return;

        let data;
        try {
          data = JSON.parse(source.textContent);
        } catch (error) {
          console.error('[localization-form] could not parse country data', error);
          return;
        }
        if (!data || !Array.isArray(data.countries) || data.countries.length === 0) return;

        const position = list.dataset.localPosition || 'Country';
        const allList = list.querySelector('ul.countries');
        const popularList = list.querySelector('ul.popular-countries');

        if (popularList && data.showPopular && Array.isArray(data.popular) && data.popular.length > 0) {
          // data.popular is already in the server-sorted order.
          const popularRows = data.popular
            .map((iso) => data.countries.find((entry) => entry[0] === iso))
            .filter(Boolean)
            .map((entry) => this.buildCountryRow(entry, data, position + '-popular'))
            .join('');
          if (popularRows) {
            popularList.innerHTML = popularRows;
            popularList.removeAttribute('hidden');
          }
        }

        if (allList) {
          allList.innerHTML = data.countries.map((entry) => this.buildCountryRow(entry, data, position)).join('');
        }

        list.dataset.hydrated = 'true';
      }

      openSelector() {
        this.hydrateCountries();
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
